import React from 'react';
import { Typography, Form, Input, Button, Select, message, Card, Row, Col, Divider, Tag, Badge, Statistic, Modal, QRCode, Result, Collapse, Switch, InputNumber, Spin, Alert } from 'antd';
import {
  SendOutlined, RocketOutlined, EyeOutlined, StarOutlined,
  ThunderboltOutlined, GlobalOutlined, SafetyCertificateOutlined,
  CheckCircleOutlined, CrownOutlined, FireOutlined,
  ClockCircleOutlined, TeamOutlined, TrophyOutlined,
  LinkOutlined, CustomerServiceOutlined, SearchOutlined,
  CheckOutlined, CloseOutlined, ArrowRightOutlined,
  WechatOutlined, AlipayCircleOutlined, DollarOutlined,
  InfoCircleOutlined, ShopOutlined, BankOutlined, GiftOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import QRCodeLib from 'qrcode';
import { useSearchParams } from 'react-router-dom';
import ImageCropUpload from '../components/ImageCropUpload';
import { useLanguage } from '../store/LanguageContext';
import { toolSubmissionAPI, categoriesAPI, orderAPI, paymentAPI } from '../services/api';
import { useAuth } from '../store/AuthContext';
import { SUBMIT_PAYMENT_ENABLED, supportEmail } from '../utils/site';
import type { Category } from '../types';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

// ==================== Hero Section ====================
const HeroSection: React.FC<{ t: (k: string) => string; isZh: boolean }> = ({ t, isZh }) => (
  <div
    style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '80px 24px 60px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* decorative circles */}
    <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
    <div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

    <div style={{ position: 'relative', zIndex: 1 }}>
      <RocketOutlined style={{ fontSize: 52, color: '#fff', marginBottom: 16 }} />
      <Title style={{ color: '#fff', fontSize: 40, marginBottom: 12, fontWeight: 800 }}>
        {t('submit.heroTitle')}
      </Title>
      <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: 18, maxWidth: 680, margin: '0 auto 28px' }}>
        {t('submit.heroSubtitle')}
      </Paragraph>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
        {[
          { icon: <CheckCircleOutlined />, text: t('submit.heroBadge1') },
          { icon: <ThunderboltOutlined />, text: t('submit.heroBadge2') },
          { icon: <GlobalOutlined />, text: t('submit.heroBadge3') },
        ].map((item, i) => (
          <div key={i} style={{ color: 'rgba(255,255,255,0.95)', fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
            {item.icon} {item.text}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 32 }}>
        <Button
          size="large"
          onClick={() => {
            const target = SUBMIT_PAYMENT_ENABLED ? 'pricing-section' : 'submit-form';
            document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' });
          }}
          style={{
            background: '#fff', color: '#667eea', fontWeight: 700, border: 'none',
            height: 52, paddingInline: 40, fontSize: 17, borderRadius: 26,
            boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
          }}
        >
          {SUBMIT_PAYMENT_ENABLED
            ? <>{t('submit.heroCtaBtn')} <ArrowRightOutlined /></>
            : <>{isZh ? '免费提交工具' : 'Submit your tool free'} <ArrowRightOutlined /></>}
        </Button>
      </div>
    </div>
  </div>
);

// ==================== Stats Bar ====================
const StatsBar: React.FC<{ t: (k: string) => string }> = ({ t }) => (
  <div style={{
    maxWidth: 960, margin: '-36px auto 0', position: 'relative', zIndex: 2, padding: '0 24px',
  }}>
    <Card style={{ borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }} bodyStyle={{ padding: '24px 16px' }}>
      <Row gutter={[16, 16]} justify="space-around" align="middle">
        {[
          { value: t('submit.stat1Value'), label: t('submit.stat1') },
          { value: t('submit.stat2Value'), label: t('submit.stat2') },
          { value: t('submit.stat3Value'), label: t('submit.stat3') },
          { value: t('submit.stat4Value'), label: t('submit.stat4') },
        ].map((s, i) => (
          <Col key={i} xs={12} sm={6} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#667eea', lineHeight: 1.2 }}>{s.value}</div>
            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{s.label}</div>
          </Col>
        ))}
      </Row>
    </Card>
  </div>
);

// ==================== Pricing Section ====================
interface PricingProps {
  t: (k: string) => string;
  isZh: boolean;
  onSelect: (plan: string) => void;
  onPremiumExposure?: () => void;
}

const PricingSection: React.FC<PricingProps> = ({ t, isZh, onSelect, onPremiumExposure }) => {
  const plans = [
    {
      key: 'free',
      name: t('submit.planFreeName'),
      price: isZh ? '¥0' : '$0',
      period: t('submit.planFreePrice'),
      color: '#52c41a',
      bg: '#f6ffed',
      border: '2px solid #b7eb8f',
      features: [
        { text: t('submit.planFreeF1'), ok: true },
        { text: t('submit.planFreeF2'), ok: true },
        { text: t('submit.planFreeF3'), ok: true },
        { text: t('submit.planFreeF4'), ok: false },
        { text: t('submit.planFreeF5'), ok: false },
        { text: t('submit.planFreeF6'), ok: false },
        { text: t('submit.planFreeF7'), ok: false },
      ],
      btnText: t('submit.planFreeBtn'),
      btnType: 'default' as const,
      popular: false,
    },
    {
      key: 'standard',
      name: t('submit.planStdName'),
      price: isZh ? '¥50' : '$7',
      period: t('submit.planStdPrice'),
      color: '#1677ff',
      bg: 'linear-gradient(135deg, #e8f4fd 0%, #f0e6ff 100%)',
      border: '2px solid #1677ff',
      features: [
        { text: t('submit.planStdF1'), ok: true },
        { text: t('submit.planStdF2'), ok: true },
        { text: t('submit.planStdF3'), ok: true },
        { text: t('submit.planStdF4'), ok: true },
        { text: t('submit.planStdF5'), ok: true },
        { text: t('submit.planStdF6'), ok: false },
        { text: t('submit.planStdF7'), ok: false },
      ],
      btnText: t('submit.planStdBtn'),
      btnType: 'primary' as const,
      popular: true,
    },
    {
      key: 'premium',
      name: t('submit.planProName'),
      price: isZh ? '¥698' : '$99',
      period: t('submit.planProPrice'),
      color: '#faad14',
      bg: 'linear-gradient(135deg, #fff7e6 0%, #fff1f0 100%)',
      border: '2px solid #faad14',
      features: [
        { text: t('submit.planProF1'), ok: true },
        { text: t('submit.planProF2'), ok: true },
        { text: t('submit.planProF3'), ok: true },
        { text: t('submit.planProF4'), ok: true },
        { text: t('submit.planProF5'), ok: true },
        { text: t('submit.planProF6'), ok: true },
        { text: t('submit.planProF7'), ok: true },
      ],
      btnText: t('submit.planProBtn'),
      btnType: 'primary' as const,
      popular: false,
    },
  ];

  return (
    <div id="pricing-section" style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 24px 48px' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: 8 }}>{t('submit.pricingTitle')}</Title>
      <Paragraph style={{ textAlign: 'center', color: '#666', fontSize: 16, marginBottom: 48 }}>
        {t('submit.pricingSubtitle')}
      </Paragraph>

      <Row gutter={[24, 24]} justify="center">
        {plans.map((plan) => (
          <Col key={plan.key} xs={24} sm={24} md={8}>
            <Badge.Ribbon
              text={plan.popular ? t('submit.popularTag') : ''}
              color="#1677ff"
              style={{ display: plan.popular ? undefined : 'none' }}
            >
              <Card
                hoverable
                style={{
                  borderRadius: 16,
                  border: plan.border,
                  height: '100%',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  boxShadow: plan.popular ? '0 12px 40px rgba(22,119,255,0.15)' : '0 4px 16px rgba(0,0,0,0.06)',
                  transform: plan.popular ? 'scale(1.03)' : undefined,
                }}
                bodyStyle={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', height: '100%' }}
              >
                {/* Plan header */}
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ marginBottom: 8 }}>
                    {plan.key === 'free' && <CheckCircleOutlined style={{ fontSize: 36, color: plan.color }} />}
                    {plan.key === 'standard' && <StarOutlined style={{ fontSize: 36, color: plan.color }} />}
                    {plan.key === 'premium' && <CrownOutlined style={{ fontSize: 36, color: plan.color }} />}
                  </div>
                  <Title level={4} style={{ margin: '8px 0 4px', color: plan.color }}>{plan.name}</Title>
                  <div>
                    <span style={{ fontSize: 40, fontWeight: 800, color: '#1a1a1a' }}>{plan.price}</span>
                    <span style={{ fontSize: 14, color: '#999', marginLeft: 4 }}>/ {plan.period}</span>
                  </div>
                </div>

                <Divider style={{ margin: '0 0 20px' }} />

                {/* Features */}
                <div style={{ flex: 1, marginBottom: 24 }}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 14 }}>
                      {f.ok
                        ? <CheckOutlined style={{ color: '#52c41a', fontSize: 14, flexShrink: 0 }} />
                        : <CloseOutlined style={{ color: '#d9d9d9', fontSize: 14, flexShrink: 0 }} />}
                      <span style={{ color: f.ok ? '#333' : '#bbb' }}>{f.text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <Button
                  type={plan.btnType}
                  size="large"
                  block
                  onClick={() => {
                    if (plan.key === 'premium' && onPremiumExposure) {
                      onPremiumExposure();
                    } else {
                      onSelect(plan.key);
                    }
                  }}
                  style={{
                    height: 48,
                    fontWeight: 600,
                    borderRadius: 10,
                    fontSize: 15,
                    ...(plan.key === 'premium' ? {
                      background: 'linear-gradient(135deg, #faad14 0%, #ff6b00 100%)',
                      border: 'none',
                      color: '#fff',
                    } : {}),
                  }}
                >
                  {plan.btnText}
                </Button>
              </Card>
            </Badge.Ribbon>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// ==================== How It Works ====================
const HowItWorksSection: React.FC<{ t: (k: string) => string }> = ({ t }) => {
  const steps = [
    { icon: <SearchOutlined style={{ fontSize: 28, color: '#667eea' }} />, title: t('submit.step1Title'), desc: t('submit.step1Desc') },
    { icon: <SendOutlined style={{ fontSize: 28, color: '#52c41a' }} />, title: t('submit.step2Title'), desc: t('submit.step2Desc') },
    { icon: <ClockCircleOutlined style={{ fontSize: 28, color: '#faad14' }} />, title: t('submit.step3Title'), desc: t('submit.step3Desc') },
    { icon: <CheckCircleOutlined style={{ fontSize: 28, color: '#ff4d4f' }} />, title: t('submit.step4Title'), desc: t('submit.step4Desc') },
  ];

  return (
    <div style={{ background: '#fff', padding: '64px 24px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 8 }}>{t('submit.howTitle')}</Title>
        <Paragraph style={{ textAlign: 'center', color: '#666', fontSize: 16, marginBottom: 48 }}>
          {t('submit.howSubtitle')}
        </Paragraph>
        <Row gutter={[32, 32]}>
          {steps.map((step, i) => (
            <Col key={i} xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%', margin: '0 auto 16px',
                  background: '#f5f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid #e8eaff', position: 'relative',
                }}>
                  {step.icon}
                  <div style={{
                    position: 'absolute', top: -4, right: -4, width: 24, height: 24, borderRadius: '50%',
                    background: '#667eea', color: '#fff', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{i + 1}</div>
                </div>
                <Title level={5} style={{ marginBottom: 8 }}>{step.title}</Title>
                <Text type="secondary" style={{ fontSize: 13 }}>{step.desc}</Text>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

// ==================== Benefits Section ====================
const BenefitsSection: React.FC<{ t: (k: string) => string }> = ({ t }) => {
  const benefits = [
    { icon: <EyeOutlined style={{ fontSize: 32, color: '#667eea' }} />, title: t('submit.benefit1Title'), desc: t('submit.benefit1Desc') },
    { icon: <StarOutlined style={{ fontSize: 32, color: '#f5a623' }} />, title: t('submit.benefit2Title'), desc: t('submit.benefit2Desc') },
    { icon: <ThunderboltOutlined style={{ fontSize: 32, color: '#52c41a' }} />, title: t('submit.benefit3Title'), desc: t('submit.benefit3Desc') },
    { icon: <SafetyCertificateOutlined style={{ fontSize: 32, color: '#1890ff' }} />, title: t('submit.benefit4Title'), desc: t('submit.benefit4Desc') },
    { icon: <LinkOutlined style={{ fontSize: 32, color: '#722ed1' }} />, title: t('submit.benefit5Title'), desc: t('submit.benefit5Desc') },
    { icon: <TeamOutlined style={{ fontSize: 32, color: '#eb2f96' }} />, title: t('submit.benefit6Title'), desc: t('submit.benefit6Desc') },
  ];

  return (
    <div style={{ maxWidth: 1060, margin: '0 auto', padding: '64px 24px' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: 8 }}>{t('submit.whyTitle')}</Title>
      <Paragraph style={{ textAlign: 'center', color: '#666', fontSize: 16, marginBottom: 48 }}>
        {t('submit.whySubtitle')}
      </Paragraph>
      <Row gutter={[24, 24]}>
        {benefits.map((b, i) => (
          <Col xs={24} sm={12} md={8} key={i}>
            <Card hoverable style={{ borderRadius: 14, height: '100%', textAlign: 'center' }} bodyStyle={{ padding: 28 }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16, margin: '0 auto 16px',
                background: '#f5f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{b.icon}</div>
              <Title level={5} style={{ marginBottom: 8 }}>{b.title}</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>{b.desc}</Text>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// ==================== Comparison Table ====================
const ComparisonSection: React.FC<{ t: (k: string) => string; isZh: boolean }> = ({ t, isZh }) => {
  const rows = [
    { label: t('submit.cmpRow1'), free: true, std: true, pro: true },
    { label: t('submit.cmpRow2'), free: '3-5' + t('submit.cmpDays'), std: '24h', pro: t('submit.cmpImmediate') },
    { label: t('submit.cmpRow3'), free: false, std: true, pro: true },
    { label: t('submit.cmpRow4'), free: false, std: true, pro: true },
    { label: t('submit.cmpRow5'), free: false, std: false, pro: '7' + t('submit.cmpDays') },
    { label: t('submit.cmpRow6'), free: false, std: false, pro: true },
    { label: t('submit.cmpRow7'), free: false, std: false, pro: true },
    { label: t('submit.cmpRow8'), free: false, std: false, pro: true },
  ];

  const renderCell = (val: boolean | string) => {
    if (val === true) return <CheckOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
    if (val === false) return <CloseOutlined style={{ color: '#d9d9d9', fontSize: 16 }} />;
    return <Text strong style={{ color: '#1677ff' }}>{val}</Text>;
  };

  return (
    <div style={{ background: '#fff', padding: '64px 24px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 48 }}>{t('submit.cmpTitle')}</Title>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', width: '34%' }}>{t('submit.cmpFeature')}</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', color: '#52c41a' }}>
                  {t('submit.planFreeName')}<br /><small style={{ color: '#999' }}>{isZh ? '¥0' : '$0'}</small>
                </th>
                <th style={{ textAlign: 'center', padding: '12px 16px', color: '#1677ff' }}>
                  {t('submit.planStdName')}<br /><small style={{ color: '#999' }}>{isZh ? '¥50' : '$7'}</small>
                </th>
                <th style={{ textAlign: 'center', padding: '12px 16px', color: '#faad14' }}>
                  {t('submit.planProName')}<br /><small style={{ color: '#999' }}>{isZh ? '¥698' : '$99'}</small>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f5f5f5', background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                  <td style={{ padding: '14px 16px', fontWeight: 500 }}>{row.label}</td>
                  <td style={{ textAlign: 'center', padding: '14px 16px' }}>{renderCell(row.free)}</td>
                  <td style={{ textAlign: 'center', padding: '14px 16px' }}>{renderCell(row.std)}</td>
                  <td style={{ textAlign: 'center', padding: '14px 16px' }}>{renderCell(row.pro)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== Submit Form ====================
interface FormProps {
  t: (k: string) => string;
  selectedPlan: string;
  isZh: boolean;
}

const SubmitForm: React.FC<FormProps> = ({ t, selectedPlan, isZh }) => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const intentFromUrl = searchParams.get('intent') || undefined;
  const [loading, setLoading] = React.useState(false);
  const [payModalVisible, setPayModalVisible] = React.useState(false);
  const [submittedPlan, setSubmittedPlan] = React.useState('');
  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [categories, setCategories] = React.useState<Category[]>([]);

  React.useEffect(() => {
    categoriesAPI.getAll().then((res: any) => {
      const cats = res.data?.categories || res.data || [];
      setCategories(cats.filter((c: Category) => c.is_active));
    }).catch(() => {});
  }, []);

  const handleSubmit = async (values: Record<string, any>) => {
    try {
      setLoading(true);
      // Payment temporarily disabled: always treat as free direct submit.
      const plan = SUBMIT_PAYMENT_ENABLED ? (values.plan || selectedPlan) : 'free';
      const userId = user?.id || 0;

      let isPaid = false;
      if (SUBMIT_PAYMENT_ENABLED && userId > 0 && plan !== 'free') {
        const paymentRes = await toolSubmissionAPI.getPaymentStatus(userId);
        isPaid = !!paymentRes.data?.isPaid;
      }

      const submissionRes = await toolSubmissionAPI.create({
        user_id: userId || undefined,
        plan_type: plan,
        tool_name: values.toolName,
        tool_url: values.toolUrl,
        category: values.category,
        description: values.description,
        availability: values.availability || 'unknown',
        tags: values.tags || '',
        contact_info: values.contact,
        // ---- 新增完整字段 ----
        name_en: values.name_en,
        name_zh: values.name_zh,
        short_description: values.short_description,
        description_en: values.description_en,
        description_zh: values.description_zh,
        logo: values.logo,
        showcase_image: values.showcase_image,
        api_doc_url: values.api_doc_url,
        pricing_type: values.pricing_type,
        pricing_detail: values.pricing_detail,
        cn_url: values.cn_url,
        cn_icp: values.cn_icp,
        cn_app_store: values.cn_app_store,
        cn_wechat_mini: values.cn_wechat_mini,
        cn_alternative_names: values.cn_alternative_names,
        supports_invoice: values.supports_invoice,
        supports_contract: values.supports_contract,
        supports_on_prem: values.supports_on_prem,
        procurement_channel: values.procurement_channel,
        procurement_contact: values.procurement_contact,
        min_seats: values.min_seats,
        enterprise_features: values.enterprise_features,
        // 平台优惠
        has_platform_discount: values.has_platform_discount,
        discount_description: values.discount_description,
        coupon_code: values.coupon_code,
        discount_url: values.discount_url,
        intent: values.intent || undefined,
      });

      const submittedStatus = submissionRes.data?.submission?.status;

      if (SUBMIT_PAYMENT_ENABLED && plan !== 'free') {
        setSubmittedPlan(plan);
        setPayModalVisible(true);
        if (isPaid || submittedStatus === 'APPROVED') {
          message.success(isZh ? '检测到您已支付，已自动通过审核并开通权益。' : 'Payment verified. Auto-approved with benefits enabled.');
        } else {
          message.info(isZh ? '已进入待审核，10小时内未支付将自动退出审核。' : 'Submission is pending review and will auto-exit in 10 hours if unpaid.');
        }
      } else {
        setSubmitSuccess(true);
        message.success(isZh ? '提交成功！我们已收到，会尽快人工审核。' : 'Submitted! We received it and will review shortly.');
      }

      form.resetFields();
      form.setFieldsValue({ plan: SUBMIT_PAYMENT_ENABLED ? selectedPlan : 'free' });
    } catch (err: any) {
      message.error(err.response?.data?.error || t('submit.failed'));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    form.setFieldsValue({ plan: selectedPlan });
  }, [selectedPlan, form]);

  const planTag = selectedPlan === 'premium'
    ? { color: '#faad14', price: isZh ? '¥698' : '$99' }
    : selectedPlan === 'standard'
      ? { color: '#1677ff', price: isZh ? '¥50' : '$7' }
      : { color: '#52c41a', price: isZh ? '¥0' : '$0' };

  const sectionStyle: React.CSSProperties = { marginBottom: 0 };
  const collapseItems = [
    {
      key: 'basic',
      label: <span><InfoCircleOutlined style={{ marginRight: 8, color: '#667eea' }} />{t('submit.section.basic')}</span>,
      children: (
        <div style={sectionStyle}>
          {/* Plan selector — hidden while online payment is off */}
          {SUBMIT_PAYMENT_ENABLED ? (
          <Form.Item
            name="plan"
            label={<span>{t('submit.planLabel')}<Tag color={planTag.color} style={{ marginLeft: 8 }}>{planTag.price}</Tag></span>}
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { label: `✅ ${t('submit.planFreeName')} — ${isZh ? '¥0' : '$0'}`, value: 'free' },
                { label: `⭐ ${t('submit.planStdName')} — ${isZh ? '¥50' : '$7'}`, value: 'standard' },
                { label: `👑 ${t('submit.planProName')} — ${isZh ? '¥698' : '$99'}`, value: 'premium' },
              ]}
            />
          </Form.Item>
          ) : (
          <>
          <Form.Item name="plan" hidden initialValue="free">
            <Input />
          </Form.Item>

          <Form.Item
            name="intent"
            label={isZh ? '融资/投资意向（可选）' : 'Funding intent (optional)'}
            initialValue={intentFromUrl === 'seeking_funding' || intentFromUrl === 'investing' ? intentFromUrl : undefined}
          >
            <Select
              allowClear
              placeholder={isZh ? '不填写也可正常提交' : 'Optional'}
              options={[
                { value: 'seeking_funding', label: isZh ? '寻求融资曝光' : 'Seeking funding' },
                { value: 'investing', label: isZh ? '投资人找标的' : 'Investor looking for deals' },
              ]}
            />
          </Form.Item>
          </>
          )}

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="toolName" label={t('submit.toolNameLabel')} rules={[{ required: true, message: t('submit.toolNameRequired') }]}>
                <Input placeholder={t('submit.toolNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="toolUrl" label={t('submit.toolUrlLabel')} rules={[{ required: true, message: t('submit.toolUrlRequired') }]}>
                <Input placeholder="https://example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="name_en" label={t('submit.nameEnLabel')}>
                <Input placeholder={t('submit.nameEnPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="name_zh" label={t('submit.nameZhLabel')}>
                <Input placeholder={t('submit.nameZhPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="short_description" label={t('submit.shortDescLabel')} rules={[{ required: true, message: t('submit.shortDescRequired') }]}>
            <Input placeholder={t('submit.shortDescPlaceholder')} maxLength={100} showCount />
          </Form.Item>

          <Form.Item name="category" label={t('submit.categoryLabel')}>
            <Select
              placeholder={t('submit.categoryPlaceholder')}
              allowClear
              showSearch
              optionFilterProp="label"
              options={categories.length > 0
                ? categories.map(c => ({ label: isZh ? (c.name_zh || c.name) : c.name, value: c.slug }))
                : [
                    { label: t('submit.catWriting'), value: 'writing' },
                    { label: t('submit.catImage'), value: 'image' },
                    { label: t('submit.catVideo'), value: 'video' },
                    { label: t('submit.catCode'), value: 'code' },
                    { label: t('submit.catChat'), value: 'chatbot' },
                    { label: t('submit.catProductivity'), value: 'productivity' },
                    { label: t('submit.catOther'), value: 'other' },
                  ]
              }
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="logo" label={t('submit.logoLabel')} tooltip={isZh ? 'Logo 将被裁剪为 1:1 正方形' : 'Logo will be cropped to 1:1 square'}>
                <ImageCropUpload
                  aspect={1}
                  hint={isZh ? '上传 Logo' : 'Upload Logo'}
                  cropTitle={isZh ? '裁剪 Logo（1:1）' : 'Crop Logo (1:1)'}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item name="showcase_image" label={t('submit.showcaseLabel')} tooltip={isZh ? '展示图将被裁剪为 16:9 比例，用于工具卡片封面' : 'Will be cropped to 16:9 for the tool card cover'}>
                <ImageCropUpload
                  aspect={16 / 9}
                  hint={isZh ? '上传展示图' : 'Upload Showcase'}
                  cropTitle={isZh ? '裁剪展示图（16:9）' : 'Crop Showcase (16:9)'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="api_doc_url" label={t('submit.apiDocLabel')}>
            <Input placeholder={t('submit.apiDocPlaceholder')} />
          </Form.Item>
        </div>
      ),
    },
    {
      key: 'description',
      label: <span><InfoCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />{t('submit.section.description')}</span>,
      children: (
        <div style={sectionStyle}>
          <Form.Item name="description" label={t('submit.descriptionLabel')} rules={[{ required: true, message: t('submit.descriptionRequired') }]}>
            <TextArea rows={5} placeholder={t('submit.descriptionPlaceholder')} showCount maxLength={2000} />
          </Form.Item>
          <Form.Item name="description_en" label={t('submit.descEnLabel')}>
            <TextArea rows={4} placeholder={t('submit.descEnPlaceholder')} showCount maxLength={2000} />
          </Form.Item>
          <Form.Item name="description_zh" label={t('submit.descZhLabel')}>
            <TextArea rows={4} placeholder={t('submit.descZhPlaceholder')} showCount maxLength={2000} />
          </Form.Item>
        </div>
      ),
    },
    {
      key: 'pricing',
      label: <span><DollarOutlined style={{ marginRight: 8, color: '#faad14' }} />{t('submit.section.pricing')}</span>,
      children: (
        <div style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="pricing_type" label={t('submit.pricingTypeLabel')}>
                <Select
                  allowClear
                  options={[
                    { label: t('submit.pricingTypeFree'), value: 'free' },
                    { label: t('submit.pricingTypeFreemium'), value: 'freemium' },
                    { label: t('submit.pricingTypePaid'), value: 'paid' },
                    { label: t('submit.pricingTypeContact'), value: 'contact' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="pricing_detail" label={t('submit.pricingDetailLabel')}>
                <Input placeholder={t('submit.pricingDetailPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'discount',
      label: <span><GiftOutlined style={{ marginRight: 8, color: '#eb2f96' }} />{t('submit.section.discount')}</span>,
      children: (
        <div style={sectionStyle}>
          <div style={{ background: '#fff7e6', border: '1px solid #ffe7ba', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#ad6800' }}>
            <GiftOutlined style={{ marginRight: 6 }} />
            {isZh
              ? '如果您愿意为通过本平台过来的用户提供专属优惠，将获得更多曝光和推荐机会！'
              : 'Tools offering exclusive discounts for our users get more exposure and recommendations!'}
          </div>
          <Form.Item name="has_platform_discount" label={t('submit.hasDiscountLabel')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="discount_description" label={t('submit.discountDescLabel')}>
                <Input placeholder={t('submit.discountDescPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="coupon_code" label={t('submit.couponCodeLabel')}>
                <Input placeholder={t('submit.couponCodePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="discount_url" label={t('submit.discountUrlLabel')}>
            <Input placeholder={t('submit.discountUrlPlaceholder')} />
          </Form.Item>
        </div>
      ),
    },
    {
      key: 'cn',
      label: <span><GlobalOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />{t('submit.section.cnAvailability')}</span>,
      children: (
        <div style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="availability" label={isZh ? '可用性' : 'Availability'}>
                <Select
                  allowClear
                  options={[
                    { label: isZh ? '可直连' : 'Direct', value: 'direct' },
                    { label: isZh ? '部分可用' : 'Partial', value: 'mirror' },
                    { label: isZh ? '访问受限' : 'Restricted', value: 'vpn' },
                    { label: isZh ? '不可用' : 'Blocked', value: 'blocked' },
                    { label: isZh ? '国产服务' : 'CN Native', value: 'cn_native' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="cn_url" label={t('submit.cnUrlLabel')}>
                <Input placeholder={t('submit.cnUrlPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="cn_icp" label={t('submit.cnIcpLabel')}>
                <Input placeholder={t('submit.cnIcpPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="cn_alternative_names" label={t('submit.cnAltNamesLabel')}>
                <Input placeholder={t('submit.cnAltNamesPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Form.Item name="cn_app_store" label={t('submit.cnAppStoreLabel')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item name="cn_wechat_mini" label={t('submit.cnWechatMiniLabel')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'enterprise',
      label: <span><BankOutlined style={{ marginRight: 8, color: '#722ed1' }} />{t('submit.section.enterprise')}</span>,
      children: (
        <div style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={8}>
              <Form.Item name="supports_invoice" label={t('submit.invoiceLabel')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="supports_contract" label={t('submit.contractLabel')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="supports_on_prem" label={t('submit.onPremLabel')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="procurement_channel" label={t('submit.procurementChannelLabel')}>
                <Select
                  allowClear
                  options={[
                    { label: t('submit.procurementDirect'), value: 'direct' },
                    { label: t('submit.procurementReseller'), value: 'reseller' },
                    { label: t('submit.procurementMarketplace'), value: 'cloud_marketplace' },
                    { label: t('submit.procurementOpensource'), value: 'opensource' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="procurement_contact" label={t('submit.procurementContactLabel')}>
                <Input placeholder={t('submit.procurementContactPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="min_seats" label={t('submit.minSeatsLabel')}>
                <InputNumber min={1} placeholder={t('submit.minSeatsPlaceholder')} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="enterprise_features" label={t('submit.enterpriseFeaturesLabel')}>
                <Input placeholder={t('submit.enterpriseFeaturesPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'contact',
      label: <span><CustomerServiceOutlined style={{ marginRight: 8, color: '#1890ff' }} />{t('submit.section.contact')}</span>,
      children: (
        <div style={sectionStyle}>
          <Form.Item name="tags" label={isZh ? '标签（逗号分隔）' : 'Tags (comma separated)'}>
            <Input placeholder={isZh ? '例如：写作, 办公, 协作' : 'e.g. writing, productivity, collaboration'} />
          </Form.Item>
          <Form.Item name="contact" label={t('submit.contactLabel')} rules={[{ required: true, message: t('submit.contactRequired') }]}>
            <Input placeholder={t('submit.contactPlaceholder')} />
          </Form.Item>
        </div>
      ),
    },
  ];

  return (
    <div id="submit-form" style={{ maxWidth: 820, margin: '0 auto', padding: '64px 24px 48px' }}>
      <Card
        style={{ borderRadius: 18, boxShadow: '0 12px 40px rgba(0,0,0,0.08)' }}
        title={
          <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
            <RocketOutlined style={{ fontSize: 28, color: '#667eea', marginBottom: 8 }} />
            <Title level={3} style={{ margin: '0 0 4px' }}>{t('submit.formTitle')}</Title>
            <Text type="secondary" style={{ fontSize: 14 }}>{t('submit.formSubtitle')}</Text>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} size="large" initialValues={{ plan: selectedPlan }}>
          <Collapse
            defaultActiveKey={['basic', 'description', 'contact']}
            ghost
            style={{ marginBottom: 16 }}
            items={collapseItems}
          />

          {/* payment notice for paid plans */}
          {SUBMIT_PAYMENT_ENABLED && selectedPlan !== 'free' && (
            <div style={{
              background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 10,
              padding: '14px 16px', marginBottom: 24, fontSize: 13, color: '#ad6800',
            }}>
              <CrownOutlined style={{ marginRight: 6 }} />
              {t('submit.paymentNotice')}
            </div>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              size="large"
              block
              loading={loading}
              style={{
                height: 52, fontWeight: 700, fontSize: 16, borderRadius: 12,
                ...(SUBMIT_PAYMENT_ENABLED && selectedPlan === 'premium' ? {
                  background: 'linear-gradient(135deg, #faad14, #ff6b00)',
                  border: 'none',
                } : {}),
              }}
            >
              {!SUBMIT_PAYMENT_ENABLED || selectedPlan === 'free' ? t('submit.submitButton') : t('submit.submitAndPay')}
            </Button>
          </Form.Item>
        </Form>

        {/* 免费提交成功提示 */}
        {submitSuccess && (
          <Result
            status="success"
            title={isZh ? '提交成功！' : 'Submission Successful!'}
            subTitle={isZh ? '我们会尽快审核您的工具，请耐心等待。' : 'We will review your tool as soon as possible.'}
            extra={<Button type="primary" onClick={() => setSubmitSuccess(false)}>{isZh ? '继续提交' : 'Submit Another'}</Button>}
            style={{ marginTop: 24 }}
          />
        )}
      </Card>

      {/* ============ 支付二维码弹窗 ============ */}
      <Modal
        open={payModalVisible}
        onCancel={() => { setPayModalVisible(false); message.success(isZh ? '提交成功！请完成支付后等待审核。' : 'Submitted! Please complete payment and wait for review.'); }}
        footer={null}
        width={520}
        centered
        title={
          <div style={{ textAlign: 'center' }}>
            <DollarOutlined style={{ fontSize: 22, color: '#faad14', marginRight: 8 }} />
            <span style={{ fontSize: 18, fontWeight: 700 }}>
              {isZh ? '扫码完成支付' : 'Complete Payment'}
            </span>
          </div>
        }
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {/* 金额 */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: 12,
            padding: '16px 24px', display: 'inline-block', marginBottom: 24,
          }}>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>
              {isZh ? '应付金额' : 'Amount Due'}
            </Text>
            <div style={{ color: '#fff', fontSize: 32, fontWeight: 800, lineHeight: 1.2 }}>
              {submittedPlan === 'premium'
                ? (isZh ? '¥698' : '$99')
                : (isZh ? '¥50' : '$7')}
            </div>
            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
              {submittedPlan === 'premium'
                ? (isZh ? '首页曝光套餐' : 'Premium Spotlight Plan')
                : (isZh ? '标准收录套餐' : 'Standard Listing Plan')}
            </Text>
          </div>

          <Divider style={{ margin: '16px 0' }}>
            {isZh ? '请选择以下方式扫码支付' : 'Scan to Pay'}
          </Divider>

          {/* 二维码区域 —— 微信 + 支付宝 */}
          <Row gutter={24} justify="center">
            <Col>
              <Card
                size="small"
                style={{ borderRadius: 12, border: '2px solid #52c41a', width: 200, textAlign: 'center' }}
                bodyStyle={{ padding: '12px 12px 8px' }}
              >
                <WechatOutlined style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }} />
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#52c41a' }}>
                  {isZh ? '微信支付' : 'WeChat Pay'}
                </div>
                {/* 使用 Ant Design QRCode 生成一个引导链接二维码，运营可替换为真实收款码 */}
                <div style={{ background: '#f6ffed', borderRadius: 8, padding: 8, display: 'inline-block' }}>
                  <QRCode
                    value={`globalaitools-pay://wechat?plan=${submittedPlan}&amount=${submittedPlan === 'premium' ? 698 : 50}`}
                    size={140}
                    color="#52c41a"
                    style={{ margin: 0 }}
                  />
                </div>
              </Card>
            </Col>
            <Col>
              <Card
                size="small"
                style={{ borderRadius: 12, border: '2px solid #1677ff', width: 200, textAlign: 'center' }}
                bodyStyle={{ padding: '12px 12px 8px' }}
              >
                <AlipayCircleOutlined style={{ fontSize: 24, color: '#1677ff', marginBottom: 8 }} />
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#1677ff' }}>
                  {isZh ? '支付宝' : 'Alipay'}
                </div>
                <div style={{ background: '#e6f4ff', borderRadius: 8, padding: 8, display: 'inline-block' }}>
                  <QRCode
                    value={`globalaitools-pay://alipay?plan=${submittedPlan}&amount=${submittedPlan === 'premium' ? 698 : 50}`}
                    size={140}
                    color="#1677ff"
                    style={{ margin: 0 }}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* 支付说明 */}
          <div style={{
            background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 10,
            padding: '14px 18px', marginTop: 20, textAlign: 'left', fontSize: 13, color: '#ad6800',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {isZh ? '💡 支付说明' : '💡 Payment Instructions'}
            </div>
            <div>{isZh
              ? '1. 打开微信/支付宝扫描上方二维码完成付款'
              : '1. Open WeChat/Alipay and scan the QR code above'}</div>
            <div>{isZh
              ? '2. 付款时请备注您的联系方式（邮箱或手机号）'
              : '2. Include your email or phone as payment note'}</div>
            <div>{isZh
              ? '3. 付款成功后我们会在 24 小时内完成审核上线'
              : '3. We will review and list your tool within 24 hours'}</div>
            <div style={{ marginTop: 6, color: '#d48806', fontWeight: 600 }}>{isZh
              ? `⚠️ 如需帮助请联系客服: ${supportEmail()}`
              : `⚠️ Need help? Contact: ${supportEmail()}`}</div>
          </div>

          {/* 我已支付 按钮 */}
          <Button
            type="primary" size="large" block
            style={{
              marginTop: 20, height: 48, fontWeight: 700, borderRadius: 10,
              background: 'linear-gradient(135deg, #52c41a, #389e0d)', border: 'none',
            }}
            icon={<CheckCircleOutlined />}
            onClick={() => {
              setPayModalVisible(false);
              message.success(isZh
                ? '感谢支付！我们会在 24 小时内审核并上线您的工具。'
                : 'Thank you! We will review and list your tool within 24 hours.');
            }}
          >
            {isZh ? '我已完成支付' : 'I Have Paid'}
          </Button>
          <div style={{ marginTop: 8 }}>
            <Button type="link" size="small" onClick={() => setPayModalVisible(false)}>
              {isZh ? '稍后支付' : 'Pay Later'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ==================== Testimonial / Trust Section ====================
const TrustSection: React.FC<{ t: (k: string) => string }> = ({ t }) => (
  <div style={{ background: '#fff', padding: '48px 24px' }}>
    <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
      <Title level={3} style={{ marginBottom: 32 }}>{t('submit.trustTitle')}</Title>
      <Row gutter={[24, 24]} justify="center">
        {[
          { icon: <SafetyCertificateOutlined style={{ fontSize: 28, color: '#52c41a' }} />, text: t('submit.trust1') },
          { icon: <CustomerServiceOutlined style={{ fontSize: 28, color: '#1677ff' }} />, text: t('submit.trust2') },
          { icon: <TrophyOutlined style={{ fontSize: 28, color: '#faad14' }} />, text: t('submit.trust3') },
          { icon: <GlobalOutlined style={{ fontSize: 28, color: '#722ed1' }} />, text: t('submit.trust4') },
        ].map((item, i) => (
          <Col key={i} xs={12} sm={6}>
            <div style={{ padding: '16px 8px' }}>
              <div style={{ marginBottom: 10 }}>{item.icon}</div>
              <Text style={{ fontSize: 13 }}>{item.text}</Text>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  </div>
);

// ==================== FAQ Section ====================
const FAQSection: React.FC<{ t: (k: string) => string }> = ({ t }) => {
  const faqs = [
    { q: t('submit.faq1Q'), a: t('submit.faq1A') },
    { q: t('submit.faq2Q'), a: t('submit.faq2A') },
    { q: t('submit.faq3Q'), a: t('submit.faq3A') },
    { q: t('submit.faq4Q'), a: t('submit.faq4A') },
    { q: t('submit.faq5Q'), a: t('submit.faq5A') },
    { q: t('submit.faq6Q'), a: t('submit.faq6A') },
  ];

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '64px 24px 48px' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: 40 }}>{t('submit.faqTitle')}</Title>
      {faqs.map((faq, i) => (
        <Card key={i} style={{ marginBottom: 14, borderRadius: 12 }} bodyStyle={{ padding: '18px 22px' }}>
          <Text strong style={{ fontSize: 15, color: '#1a1a1a' }}>{faq.q}</Text>
          <Paragraph type="secondary" style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.7 }}>{faq.a}</Paragraph>
        </Card>
      ))}
    </div>
  );
};

// ==================== Final CTA ====================
const FinalCTA: React.FC<{ t: (k: string) => string }> = ({ t }) => (
  <div style={{
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '64px 24px', textAlign: 'center',
  }}>
    <Title level={2} style={{ color: '#fff', marginBottom: 12 }}>{t('submit.ctaTitle')}</Title>
    <Paragraph style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, marginBottom: 32 }}>
      {t('submit.ctaDesc')}
    </Paragraph>
    <Button
      size="large"
      onClick={() => {
        const el = document.getElementById('submit-form');
        el?.scrollIntoView({ behavior: 'smooth' });
      }}
      style={{
        background: '#fff', color: '#667eea', fontWeight: 700, border: 'none',
        height: 52, paddingInline: 40, fontSize: 17, borderRadius: 26,
        boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
      }}
    >
      {t('submit.ctaBtn')} <ArrowRightOutlined />
    </Button>
  </div>
);

// ==================== 首页曝光 Modal ====================
const HomepageExposureModal: React.FC<{ visible: boolean; onClose: () => void; isZh: boolean }> = ({ visible, onClose, isZh }) => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [submissions, setSubmissions] = React.useState<any[]>([]);
  const [loadingSubs, setLoadingSubs] = React.useState(false);
  const [methods, setMethods] = React.useState<any[]>([]);
  const [selectedMethod, setSelectedMethod] = React.useState('');
  const [priceDisplay, setPriceDisplay] = React.useState(isZh ? '¥698' : '$99');
  const [step, setStep] = React.useState<'form' | 'pay'>('form');
  const [submitting, setSubmitting] = React.useState(false);
  const [qrDataUrl, setQrDataUrl] = React.useState('');
  const [staticQrMethod, setStaticQrMethod] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (!visible) {
      form.resetFields();
      setStep('form');
      setQrDataUrl('');
      return;
    }
    // 加载用户已提交的工具列表
    if (user?.id) {
      setLoadingSubs(true);
      toolSubmissionAPI.getMy(user.id)
        .then((res: any) => {
          const subs = res.data?.submissions || [];
          setSubmissions(subs);
        })
        .catch(() => {})
        .finally(() => setLoadingSubs(false));
    }
    // 加载支付方式
    paymentAPI.getPaymentMethods({ lang: isZh ? 'zh-CN' : 'en-US', plan: 'premium' })
      .then((res: any) => {
        const ms = res.data?.methods || [];
        setMethods(ms);
        setPriceDisplay(res.data?.price?.display || (isZh ? '¥698' : '$99'));
        if (ms.length > 0) setSelectedMethod(ms[0].key);
        // 检测静态二维码方式（有 qrCode 图片的）
        setStaticQrMethod(ms.filter((m: any) => m.qrCode));
      })
      .catch(() => {});
  }, [visible, user?.id, isZh]);

  const handleSubmitAndPay = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // 找到选择的 submission 名字
      const selSub = submissions.find((s: any) => s.id === values.tool_submission_id);
      const toolName = selSub?.tool_name || values.tool_name_manual || '首页曝光工具';
      const toolId = selSub?.tool_id || undefined;

      // 创建订单
      const orderRes = await orderAPI.createPublic({
        product_id: 'premium',
        tool_name: toolName,
        tool_id: toolId,
        contact_info: values.contact,
        user_contact: values.contact,
        payment_method: selectedMethod,
      });
      const newOrder = orderRes.data.order;

      // 如果有静态 QR 图片（配置了微信/支付宝收款码），直接展示
      // 否则调用 createPayment 生成动态二维码
      if (staticQrMethod.length > 0) {
        // 直接跳到支付步骤，显示静态二维码
        setStep('pay');
      } else {
        try {
          const payRes = await orderAPI.createPayment(newOrder.id, selectedMethod);
          const payData = payRes.data;
          if (payData.qr_url) {
            const dataUrl = await QRCodeLib.toDataURL(payData.qr_url, { width: 220, margin: 2 });
            setQrDataUrl(dataUrl);
          }
        } catch {
          // 如果生成动态支付码失败，依然显示确认步骤
        }
        setStep('pay');
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || (isZh ? '操作失败，请重试' : 'Operation failed, please retry'));
    } finally {
      setSubmitting(false);
    }
  };

  const toolOptions = submissions.map((s: any) => ({
    label: s.tool_name,
    value: s.id,
  }));

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={560}
      centered
      destroyOnClose
      title={
        <div style={{ textAlign: 'center', paddingTop: 8 }}>
          <CrownOutlined style={{ fontSize: 26, color: '#faad14', marginRight: 8 }} />
          <span style={{ fontSize: 18, fontWeight: 700 }}>
            {isZh ? '首页曝光 — 开通 Premium' : 'Homepage Exposure — Premium'}
          </span>
        </div>
      }
    >
      {/* ===== Step 1: 选择工具 + 联系方式 ===== */}
      {step === 'form' && (
        <div>
          {/* 价格展示 */}
          <div style={{
            background: 'linear-gradient(135deg, #faad14, #ff6b00)',
            borderRadius: 12, padding: '14px 20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 24,
          }}>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                {isZh ? '首页高权重曝光 · 365天' : 'Homepage Priority Boost · 365 days'}
              </div>
              <div style={{ color: '#fff', fontSize: 28, fontWeight: 800, lineHeight: 1.2 }}>
                {priceDisplay}
              </div>
            </div>
            <CrownOutlined style={{ fontSize: 42, color: 'rgba(255,255,255,0.5)' }} />
          </div>

          <Form form={form} layout="vertical">
            {/* 工具选择 */}
            {loadingSubs ? (
              <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
            ) : toolOptions.length > 0 ? (
              <Form.Item
                name="tool_submission_id"
                label={isZh ? '选择您要曝光的工具' : 'Select your tool to expose'}
                rules={[{ required: true, message: isZh ? '请选择工具' : 'Please select a tool' }]}
              >
                <Select
                  placeholder={isZh ? '从您已提交的工具中选择...' : 'Select from your submitted tools...'}
                  options={toolOptions}
                  size="large"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            ) : (
              <>
                <Alert
                  type="warning"
                  showIcon
                  message={isZh
                    ? user?.id ? '您暂无已提交的工具，请先在下方表单中提交工具，再购买首页曝光。' : '请先登录后再选择工具'
                    : user?.id ? 'You have no submitted tools yet. Please submit a tool below first.' : 'Please log in to select your tool'}
                  style={{ marginBottom: 16 }}
                />
                <Form.Item
                  name="tool_name_manual"
                  label={isZh ? '工具名称（手动输入）' : 'Tool name (enter manually)'}
                  rules={[{ required: true, message: isZh ? '请填写工具名称' : 'Please enter tool name' }]}
                >
                  <Input placeholder={isZh ? '请输入您的工具名称' : 'Enter your tool name'} size="large" />
                </Form.Item>
              </>
            )}

            {/* 联系方式 */}
            <Form.Item
              name="contact"
              label={isZh ? '联系方式（邮箱/手机）' : 'Contact (email/phone)'}
              rules={[{ required: true, message: isZh ? '请填写联系方式' : 'Please enter contact info' }]}
            >
              <Input
                placeholder={isZh ? '支付后用于确认权益归属' : 'Used for benefit confirmation after payment'}
                size="large"
              />
            </Form.Item>

            {/* 支付方式选择 */}
            {methods.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }}>{isZh ? '选择支付方式' : 'Payment Method'}</Divider>
                <Row gutter={12} style={{ marginBottom: 16 }}>
                  {methods.map((m: any) => (
                    <Col key={m.key} span={24 / methods.length}>
                      <Card
                        hoverable
                        size="small"
                        style={{
                          textAlign: 'center',
                          border: selectedMethod === m.key ? `2px solid ${m.color}` : '2px solid transparent',
                          cursor: 'pointer',
                          borderRadius: 10,
                        }}
                        onClick={() => setSelectedMethod(m.key)}
                        bodyStyle={{ padding: '10px 8px' }}
                      >
                        <div style={{ color: m.color, fontWeight: 600, fontSize: 14 }}>{m.name}</div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </Form>

          <Button
            type="primary"
            size="large"
            block
            loading={submitting}
            onClick={handleSubmitAndPay}
            style={{
              height: 50, fontWeight: 700, fontSize: 16, borderRadius: 10,
              background: 'linear-gradient(135deg, #faad14, #ff6b00)',
              border: 'none',
            }}
            icon={<DollarOutlined />}
          >
            {isZh ? '提交并付款' : 'Submit & Pay'}
          </Button>
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Button type="link" size="small" onClick={onClose}>
              {isZh ? '稍后再说' : 'Maybe later'}
            </Button>
          </div>
        </div>
      )}

      {/* ===== Step 2: 展示付款码 ===== */}
      {step === 'pay' && (() => {
        const isWechat = selectedMethod === 'wechat';
        const isAlipay = selectedMethod === 'alipay';
        const methodColor = isWechat ? '#07C160' : isAlipay ? '#1677ff' : '#faad14';
        const methodBg = isWechat
          ? 'linear-gradient(160deg, #f0fff4, #e8faf0)'
          : isAlipay
            ? 'linear-gradient(160deg, #e8f4ff, #f0f5ff)'
            : 'linear-gradient(160deg, #fffbe6, #fff7e6)';
        const methodName = isWechat
          ? (isZh ? '微信支付' : 'WeChat Pay')
          : isAlipay
            ? (isZh ? '支付宝' : 'Alipay')
            : (isZh ? '扫码支付' : 'Scan to Pay');
        const MethodIcon = isWechat ? WechatOutlined : AlipayCircleOutlined;
        const activeStaticMethod = staticQrMethod.find((m: any) => m.key === selectedMethod) || staticQrMethod[0];

        return (
          <div style={{ textAlign: 'center', padding: '4px 0' }}>
            {/* 顶部：金额 + 方式信息栏 */}
            <div style={{
              background: `linear-gradient(135deg, ${methodColor}18, ${methodColor}08)`,
              border: `1.5px solid ${methodColor}30`,
              borderRadius: 16, padding: '16px 20px', marginBottom: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ textAlign: 'left' }}>
                <div style={{ color: '#aaa', fontSize: 12, marginBottom: 2 }}>
                  {isZh ? '首页曝光权益 · 365天' : 'Homepage Exposure · 365 days'}
                </div>
                <div style={{ fontSize: 30, fontWeight: 800, color: methodColor, lineHeight: 1.1 }}>
                  {priceDisplay}
                </div>
                <div style={{ color: '#bbb', fontSize: 11, marginTop: 2 }}>
                  {isZh ? '支付后24h内开通' : 'Activated within 24h'}
                </div>
              </div>
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: `linear-gradient(135deg, ${methodColor}ee, ${methodColor}99)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 6px 18px ${methodColor}40`,
                flexShrink: 0,
              }}>
                <MethodIcon style={{ fontSize: 28, color: '#fff' }} />
              </div>
            </div>

            {/* 二维码卡片 */}
            <div style={{
              background: methodBg,
              border: `2px solid ${methodColor}25`,
              borderRadius: 20, padding: '28px 20px 20px',
              marginBottom: 20, position: 'relative',
            }}>
              {/* 悬浮标牌 */}
              <div style={{
                position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                background: `linear-gradient(135deg, ${methodColor}, ${methodColor}cc)`,
                color: '#fff', fontSize: 13, fontWeight: 700,
                padding: '4px 20px', borderRadius: 20, whiteSpace: 'nowrap',
                boxShadow: `0 3px 10px ${methodColor}50`,
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <MethodIcon style={{ fontSize: 14 }} />
                {methodName}
              </div>

              {/* 二维码 */}
              {qrDataUrl ? (
                <div>
                  <div style={{ display: 'inline-block', background: '#fff', borderRadius: 14, padding: 12, boxShadow: `0 4px 24px ${methodColor}20` }}>
                    <img src={qrDataUrl} alt="Payment QR" width={188}
                      style={{ borderRadius: 8, display: 'block' }} />
                  </div>
                  <div style={{ marginTop: 12, color: methodColor, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontWeight: 500 }}>
                    <LoadingOutlined />
                    {isZh ? '等待扫码支付...' : 'Waiting for scan...'}
                  </div>
                </div>
              ) : activeStaticMethod?.qrCode ? (
                <div style={{ display: 'inline-block', background: '#fff', borderRadius: 14, padding: 10, boxShadow: `0 4px 24px ${methodColor}20` }}>
                  <img src={activeStaticMethod.qrCode} alt={methodName} width={188}
                    style={{ borderRadius: 8, display: 'block' }} />
                </div>
              ) : (
                <div style={{ display: 'inline-block', background: '#fff', borderRadius: 14, padding: 12, boxShadow: `0 4px 24px ${methodColor}20` }}>
                  <QRCode
                    value={activeStaticMethod?.pay_link || `globalaitools-pay://${selectedMethod || 'wechat'}?plan=premium&amount=698`}
                    size={188}
                    color={methodColor}
                    style={{ margin: 0, display: 'block' }}
                  />
                </div>
              )}

              {/* 扫码提示 */}
              <div style={{ marginTop: 14, color: methodColor, fontSize: 13, fontWeight: 600 }}>
                {isWechat
                  ? (isZh ? '请用微信「扫一扫」扫描此码' : 'Open WeChat → Scan QR Code')
                  : (isZh ? '请用支付宝「扫一扫」扫描此码' : 'Open Alipay → Scan QR Code')}
              </div>
            </div>

            {/* 步骤说明 */}
            <div style={{
              background: '#fafafa', border: '1px solid #f0f0f0',
              borderRadius: 12, padding: '12px 16px', textAlign: 'left',
              fontSize: 13, color: '#666', marginBottom: 20, lineHeight: 1.9,
            }}>
              <div style={{ fontWeight: 600, color: '#333', marginBottom: 4 }}>💡 {isZh ? '操作步骤' : 'How to pay'}</div>
              {isZh ? (
                <>
                  <div>① 打开 <span style={{ color: methodColor, fontWeight: 600 }}>{methodName}</span>，点击"扫一扫"</div>
                  <div>② 对准上方二维码完成扫描付款</div>
                  <div>③ 付款备注留下联系方式，以便确认权益</div>
                </>
              ) : (
                <>
                  <div>① Open <span style={{ color: methodColor, fontWeight: 600 }}>{methodName}</span> and tap "Scan"</div>
                  <div>② Scan the QR code above to complete payment</div>
                  <div>③ Add your contact info as a payment note</div>
                </>
              )}
            </div>

            {/* 我已付款按钮 */}
            <Button
              type="primary" size="large" block
              icon={<CheckCircleOutlined />}
              style={{
                height: 50, fontWeight: 700, fontSize: 15, borderRadius: 12,
                background: `linear-gradient(135deg, ${methodColor}ee, ${methodColor}aa)`,
                border: 'none',
                boxShadow: `0 6px 18px ${methodColor}40`,
              }}
              onClick={() => {
                onClose();
                message.success(isZh
                  ? '感谢支付！24小时内开通首页曝光权益。'
                  : 'Thank you! Homepage exposure activated within 24 hours.');
              }}
            >
              {isZh ? '✅ 我已完成支付' : '✅ I Have Paid'}
            </Button>
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center', gap: 20 }}>
              <Button type="link" size="small" onClick={() => setStep('form')}>
                {isZh ? '← 返回修改' : '← Back'}
              </Button>
              <Button type="link" size="small" style={{ color: '#bbb' }} onClick={onClose}>
                {isZh ? '稍后支付' : 'Pay later'}
              </Button>
            </div>
          </div>
        );
      })()}
    </Modal>
  );
};

// ==================== Main Page ====================
const SubmitToolPage: React.FC = () => {
  const { t, language } = useLanguage();
  const isZh = language === 'zh-CN';
  const [selectedPlan, setSelectedPlan] = React.useState(SUBMIT_PAYMENT_ENABLED ? 'standard' : 'free');
  const [exposureModalVisible, setExposureModalVisible] = React.useState(false);

  const handlePlanSelect = (plan: string) => {
    setSelectedPlan(plan);
    setTimeout(() => {
      const el = document.getElementById('submit-form');
      el?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div style={{ background: '#f7f8fc', minHeight: '100vh' }}>
      <HeroSection t={t} isZh={isZh} />
      <StatsBar t={t} />
      {!SUBMIT_PAYMENT_ENABLED && (
        <div style={{ maxWidth: 820, margin: '24px auto 0', padding: '0 24px' }}>
          <Alert
            type="info"
            showIcon
            message={isZh ? '当前开放免费提交' : 'Free submissions are open'}
            description={
              isZh
                ? '在线支付暂未开通。请直接填写下方表单提交工具，我们会在后台收到并人工审核。付费置顶/加速通道稍后上线。'
                : 'Online payment is temporarily unavailable. Submit your tool with the form below — we receive it in admin and review manually. Paid featured listing will return later.'
            }
          />
        </div>
      )}
      {SUBMIT_PAYMENT_ENABLED && (
        <PricingSection
          t={t}
          isZh={isZh}
          onSelect={handlePlanSelect}
          onPremiumExposure={() => setExposureModalVisible(true)}
        />
      )}
      {SUBMIT_PAYMENT_ENABLED && <ComparisonSection t={t} isZh={isZh} />}
      <HowItWorksSection t={t} />
      <BenefitsSection t={t} />
      <SubmitForm t={t} selectedPlan={selectedPlan} isZh={isZh} />
      <TrustSection t={t} />
      <FAQSection t={t} />
      <FinalCTA t={t} />

      {SUBMIT_PAYMENT_ENABLED && (
        <HomepageExposureModal
          visible={exposureModalVisible}
          onClose={() => setExposureModalVisible(false)}
          isZh={isZh}
        />
      )}
    </div>
  );
};

export default SubmitToolPage;
